import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { freight } from "../lib/freightApi.js";
import { StaffShell, Panel, Button, Input, TextArea, Select, Note, Micro, Tag, Meter, T, mono, STAGE_LABEL, errText, mailError, fmtPhone, fmtDate } from "./ui.jsx";

/**
 * Our admin: one carrier's setup (portal plan slices 7-9; the schematic's carrier setup board).
 *
 * What the setup email brought back is keyed in here, and each answer goes where the carrier's own
 * screens read it: its people, drivers, past applicants, recruiter and hiring standards. Answers with
 * no home of their own are kept as the setup's answers. The page counts what is missing, emails for
 * only that, and moves the carrier on (setup → rehearsal → live) once the server agrees its list is
 * complete.
 *
 * ⛔ WHAT THE CARRIER SAYS IS RECORDED AS THEIRS. Their authority, and that their drivers or
 * applicants agreed to automated calls, are kept with who at the carrier said it and in which email.
 * We are recorded only as the one who keyed it in.
 */

const ROLE_LABEL = { manager: "Manager", dispatcher: "Dispatcher" };
/** The member's role as the carrier's Admins and access board names it (backend access.mjs). */
const roleLabel = (m) => (m.role === "manager" ? ROLE_LABEL.manager
  : m.access === "hiring" ? "Recruiter" : m.access === "own" ? "Dispatcher, own drivers only" : ROLE_LABEL.dispatcher);
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"];

/* ── the setup email ────────────────────────────────────────────────────────── */

export function SetupEmailPanel({ carrier, emails, missingCount, onSent }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const fullSent = emails.find((e) => e.kind === "full" && e.sent);
  const last = emails.find((e) => e.kind !== "test");
  const run = async (what, fn) => {
    setBusy(what); setMsg(null);
    try { await fn(); } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(null); }
  };
  const show = (kind) => run(`preview-${kind}`, async () => setPreview(await freight.staff.preview(carrier.id, kind)));
  const send = (kind) => run(`send-${kind}`, async () => {
    const e = await freight.staff.sendSetupEmail(carrier.id, kind);
    setMsg(e.sent ? { ok: true, text: `Sent to ${e.to}. Their reply comes to ${e.replyTo}.` }
      : e.error === "timed_out_may_have_sent" ? { ok: false, text: `Unclear: ${mailError(e.error)}.` }
        : { ok: false, text: `Not sent: ${e.error}. Nothing reached ${e.to}.` });
    setPreview(null);
    onSent();
  });
  const test = (kind) => run(`test-${kind}`, async () => {
    const e = await freight.staff.sendTest(kind, carrier.id);
    setMsg(e.sent ? { ok: true, text: `A test went to ${e.to}.` } : { ok: false, text: `The test was not sent: ${mailError(e.error)}.` });
  });
  return (
    <Panel id="email-title" title="Setup email"
      note={carrier.contactEmail ? `To ${carrier.contactName ? `${carrier.contactName}, ` : ""}${carrier.contactEmail}. Their reply comes to you.` : "No contact email yet: add one in the company details."}>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontSize: 13.5 }}>
          {last ? `${last.kind === "full" ? "The setup email" : "The missing items"} ${last.sent ? "was sent" : last.error === "timed_out_may_have_sent" ? "may have gone" : "did NOT send"} ${fmtDate(last.at)}${last.sent ? ` to ${last.to}` : `: ${mailError(last.error)}`}.`
            : "Not sent yet."}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button kind="quiet" size="sm" onClick={() => show("full")} disabled={!!busy}>Preview the setup email</Button>
          <Button size="sm" onClick={() => send("full")} disabled={!!busy || !carrier.contactEmail}>{fullSent ? "Send it again" : "Send the setup email"}</Button>
          {fullSent && missingCount > 0 && (
            <>
              <Button kind="quiet" size="sm" onClick={() => show("missing")} disabled={!!busy}>Preview the missing items</Button>
              <Button size="sm" onClick={() => send("missing")} disabled={!!busy}>Email them the {missingCount} missing {missingCount === 1 ? "item" : "items"}</Button>
            </>
          )}
          <Button kind="quiet" size="sm" onClick={() => test(missingCount && fullSent ? "missing" : "full")} disabled={!!busy}>Send a test to me</Button>
        </div>
        <Note msg={msg} />
        {preview && (
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: 14, display: "grid", gap: 8 }}>
            <div style={{ fontSize: 12.5, color: T.ink2 }}>To {preview.to || "(no contact email)"} · Reply-To {preview.replyTo}{preview.attachments.length ? ` · Attached: ${preview.attachments.join(", ")}` : ""}</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{preview.subject}</div>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", font: "inherit", fontSize: 13.5, lineHeight: 1.5, maxHeight: 420, overflow: "auto" }}>{preview.text}</pre>
            <div><Button kind="quiet" size="sm" onClick={() => setPreview(null)}>Close the preview</Button></div>
          </div>
        )}
      </div>
    </Panel>
  );
}

/* ── the answers ────────────────────────────────────────────────────────────── */

export function AnswersPanel({ setup }) {
  return (
    <Panel id="answers-title" title="Answers" right={<span style={{ fontSize: 14, fontWeight: 600 }}>{setup.answered} of {setup.total} answered</span>}>
      <div style={{ display: "grid", gap: 12 }}>
        <Meter value={setup.answered} max={setup.total} label="Answers received" />
        {setup.missing.length ? (
          <div>
            <h3 style={{ margin: "4px 0 6px", fontSize: 14, fontWeight: 600 }}>Still missing ({setup.missing.length})</h3>
            <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 3, fontSize: 13.5 }}>
              {setup.missing.map((m) => <li key={m.id}>{m.label} <span style={{ color: T.ink3 }}>· {m.section}</span></li>)}
            </ul>
          </div>
        ) : <div style={{ fontSize: 13.5 }}>Every question is answered or n/a.</div>}
      </div>
    </Panel>
  );
}

const MARK = { answered: "✓ Answered", na: "n/a", missing: "Missing" };
const REAL_HOME = { mc_dot: "the company details", owner: "People", admins: "People", dispatchers: "People", drivers: "Drivers",
  recruiters: "Hiring", past_applicants: "Past applicants", standards: "Hiring" };

export function QuestionRow({ carrierId, q, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(typeof q.value === "string" ? q.value : "");
  const [saidBy, setSaidBy] = useState(q.saidBy || "");
  const [saidIn, setSaidIn] = useState(q.saidIn || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const save = async (body) => {
    setBusy(true); setMsg(null);
    try { onSaved(await freight.staff.saveAnswer(carrierId, q.id, body)); setMsg({ ok: true, text: "Saved." }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const real = q.home === "real";
  return (
    <li style={{ borderTop: `1px solid ${T.hairSoft}`, padding: "10px 0" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="stf-btn"
        style={{ width: "100%", display: "flex", gap: "4px 14px", alignItems: "baseline", flexWrap: "wrap", background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer", color: T.ink }}>
        <span style={{ flex: "0 0 110px", fontSize: 12.5, fontWeight: 600, color: q.status === "missing" ? T.ink : T.ink3 }}>{MARK[q.status]}</span>
        <span style={{ flex: "1 1 200px", fontSize: 14, fontWeight: 500 }}>{q.label}</span>
        <span style={{ flex: "2 1 260px", fontSize: 13, color: T.ink2, minWidth: 0, overflowWrap: "anywhere" }}>{q.summary || ""}</span>
      </button>
      {open && (
        <div style={{ display: "grid", gap: 10, padding: "12px 0 4px" }}>
          <div style={{ fontSize: 12.5, color: T.ink3 }}>The email asks: “{q.ask}”</div>
          {real && <div style={{ fontSize: 12.5, color: T.ink2 }}>Answered by what is on file in {REAL_HOME[q.id] || "the carrier's records"}. A note here does not answer it.</div>}
          <TextArea label={real ? (q.statement ? "What the carrier said" : "Note") : "Their answer"} value={value} onChange={setValue} rows={real ? 2 : 3} />
          {q.statement && (
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <Input label="Who at the carrier said it" value={saidBy} onChange={setSaidBy} placeholder="Dana Owner" />
              <Input label="Where" value={saidIn} onChange={setSaidIn} placeholder="their email of October 29" />
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Button size="sm" disabled={busy} onClick={() => save({ value, saidBy, saidIn })}>Save</Button>
            {q.id !== "mc_dot" && (
              <Button size="sm" kind="quiet" disabled={busy || (q.statement && (!saidBy.trim() || !saidIn.trim()))}
                title={q.statement ? "Fill in who said it and where first" : undefined}
                onClick={() => save(q.statement ? { na: true, saidBy, saidIn } : { na: true })}>Mark n/a</Button>
            )}
            {(q.value || q.na) && <Button size="sm" kind="quiet" disabled={busy} onClick={() => save({ value: null, na: false })}>Clear</Button>}
            {q.enteredBy && <span style={{ fontSize: 12, color: T.ink3 }}>Keyed in by {q.enteredBy}, {fmtDate(q.enteredAt)}</span>}
          </div>
          <Note msg={msg} />
        </div>
      )}
    </li>
  );
}

export function QuestionsPanel({ carrierId, setup, onSaved }) {
  return (
    <Panel id="questions-title" title="The setup email’s questions" note="Open a question to key in the carrier’s answer, or mark it n/a.">
      <div style={{ display: "grid", gap: 16 }}>
        {setup.sections.map((s, i) => (
          <div key={s.id}>
            <h3 style={{ margin: "0 0 2px", fontSize: 14.5, fontWeight: 600 }}>{i + 1}. {s.title}</h3>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {s.questions.map((q) => <QuestionRow key={q.id} carrierId={carrierId} q={q} onSaved={onSaved} />)}
            </ul>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ── company details, FMCSA, the caller ID ──────────────────────────────────── */

export function DetailsPanel({ carrier, onSaved }) {
  const [f, setF] = useState({ name: carrier.name || "", mcNumber: carrier.mcNumber || "", dotNumber: carrier.dotNumber || "",
    timezone: carrier.timezone || "America/Chicago", contactName: carrier.contactName || "", contactEmail: carrier.contactEmail || "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState(null);
  const [line, setLine] = useState("");
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const act = async (fn, ok) => {
    setBusy(true); setMsg(null);
    try { onSaved(await fn()); setMsg({ ok: true, text: ok }); } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };
  const loadLines = async () => {
    try { const r = await freight.staff.lines(); setLines(r); setLine(r.lines?.[0]?.phoneNumber || ""); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
  };
  const fm = carrier.fmcsa;
  return (
    <Panel id="details-title" title="Company details" note="Kept by us: the carrier’s own people cannot change these.">
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Input label="Name Alfred says on calls" value={f.name} onChange={set("name")} />
          <Input label="MC number" value={f.mcNumber} onChange={set("mcNumber")} />
          <Input label="USDOT" value={f.dotNumber} onChange={set("dotNumber")} />
          <Select label="Time zone" value={f.timezone} onChange={set("timezone")}
            options={[...new Set([f.timezone, ...TIMEZONES])].map((z) => ({ v: z, label: z.replace("America/", "").replace("_", " ") }))} />
          <Input label="Primary contact" value={f.contactName} onChange={set("contactName")} />
          <Input label="Contact email" type="email" value={f.contactEmail} onChange={set("contactEmail")} />
        </div>
        <div><Button size="sm" disabled={busy} onClick={() => act(() => freight.staff.saveDetails(carrier.id, f), "Saved.")}>Save the details</Button></div>

        <div style={{ borderTop: `1px solid ${T.hairSoft}`, paddingTop: 12, display: "grid", gap: 6 }}>
          <Micro>FMCSA</Micro>
          <div style={{ fontSize: 13.5 }}>
            {!fm ? "Never looked up."
              : fm.configured === false ? "The lookup is not connected, so nothing was checked."
                : fm.error ? `The lookup failed (${fm.error}).`
                  : fm.found === false ? "FMCSA has no carrier with that number."
                    : `${fm.carrierName || "Found"} · ${fm.status}${fm.authorized ? " · authorized" : " · NOT authorized"}${fm.mock ? " · mock data, not a real lookup" : ""}`}
            {fm?.checkedAt && <span style={{ color: T.ink3 }}> · checked {fmtDate(fm.checkedAt)}{fm.checkedBy ? ` by ${fm.checkedBy}` : ""}</span>}
          </div>
          <div><Button size="sm" kind="quiet" disabled={busy} onClick={() => act(() => freight.staff.lookup(carrier.id), "Looked up again.")}>Ask FMCSA again</Button></div>
        </div>

        <div style={{ borderTop: `1px solid ${T.hairSoft}`, paddingTop: 12, display: "grid", gap: 8 }}>
          <Micro>The line Alfred calls from</Micro>
          <div style={{ fontSize: 13.5 }}>{carrier.phone ? fmtPhone(carrier.phone) : "None yet. Only one of the platform’s own lines can be used."}</div>
          {!lines && <div><Button size="sm" kind="quiet" onClick={loadLines}>Pick a line</Button></div>}
          {lines && (lines.configured === false ? <div style={{ fontSize: 12.5, color: T.ink3 }}>The voice platform is not connected.</div>
            : lines.error ? (
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, color: T.ink }}>
                The platform’s line list could not be read ({lines.error}).
                <Button size="sm" kind="quiet" onClick={loadLines}>Try again</Button>
              </div>
            )
            : !lines.lines?.length ? <div style={{ fontSize: 12.5, color: T.ink3 }}>The platform has no lines for this account yet.</div>
              : (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <Select label="Line" value={line} onChange={setLine} options={lines.lines.map((l) => ({ v: l.phoneNumber, label: `${fmtPhone(l.phoneNumber)} (${l.type})` }))} />
                  <Button size="sm" disabled={busy || !line} onClick={() => act(() => freight.staff.setCallerId(carrier.id, line), "The line is set.")}>Use this line</Button>
                </div>
              ))}
        </div>
        <Note msg={msg} />
      </div>
    </Panel>
  );
}

/* ── the carrier's people ───────────────────────────────────────────────────── */

export function PeoplePanel({ carrier, members, onChanged }) {
  const [f, setF] = useState({ email: "", name: "", phone: "", role: "dispatcher", owner: false });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const act = async (fn, ok) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg(ok ? { ok: true, text: ok } : null); onChanged(); } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };
  const canInvite = carrier.stage === "rehearsal" || carrier.stage === "live";
  return (
    <Panel id="people-title" title="People" note="Who signs in to the carrier’s dashboard. They sign in only once invited, and only while the carrier is in rehearsal or live.">
      <div style={{ display: "grid", gap: 14 }}>
        {members.length ? (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {members.map((m) => (
              <li key={m.id} style={{ display: "flex", gap: "6px 14px", alignItems: "center", flexWrap: "wrap", padding: "10px 0", borderTop: `1px solid ${T.hairSoft}`, opacity: m.active ? 1 : 0.55 }}>
                <span style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 500 }}>{m.name || m.email} {m.owner && <Tag>Owner</Tag>}</span>
                  <span style={{ display: "block", fontSize: 12.5, color: T.ink3, overflowWrap: "anywhere" }}>{m.email}{m.phone ? ` · ${fmtPhone(m.phone)}` : ""}</span>
                </span>
                <span style={{ flex: "0 1 220px", fontSize: 12.5, color: T.ink2 }}>
                  {roleLabel(m)}{!m.active ? " · removed" : m.invitedAt ? ` · invited ${fmtDate(m.invitedAt)}` : " · not invited"}
                  {m.active && m.inviteError && <span style={{ display: "block", color: T.ink, fontWeight: 500 }}>
                    {m.inviteError === "timed_out_may_have_sent" ? `The last invite may have gone: ${mailError(m.inviteError)}.` : `The last invite did not send: ${m.inviteError}`}
                  </span>}
                  {m.allowlisted && <span style={{ display: "block", color: T.ink }}>On ALLOWED_EMAILS: signs in as one of us. Take it off that list before inviting.</span>}
                </span>
                <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {m.active && !m.owner && <Button size="sm" kind="quiet" disabled={busy} onClick={() => act(() => freight.staff.updateMember(carrier.id, m.id, { role: "manager", owner: true }), "Owner changed.")}>Make owner</Button>}
                  {m.active && !m.owner && <Button size="sm" kind="quiet" disabled={busy} onClick={() => act(() => freight.staff.updateMember(carrier.id, m.id, { role: m.role === "manager" ? "dispatcher" : "manager" }))}>{m.role === "manager" ? "Make dispatcher" : "Make manager"}</Button>}
                  {m.active && canInvite && !m.allowlisted && <Button size="sm" kind={m.invitedAt && !m.inviteError ? "quiet" : "primary"} disabled={busy} onClick={() => act(async () => {
                    const [i] = await freight.staff.invite(carrier.id, [m.id]);
                    if (!i?.sent) {
                      onChanged();   // so the row shows what happened to this send
                      throw new Error(i?.message || (i?.error === "timed_out_may_have_sent"
                        ? `The invite to ${m.email} may have gone: ${mailError(i.error)}.`
                        : `The invite to ${m.email} was not sent${i?.error ? `: ${i.error}` : ""}.`));
                    }
                  }, `Invite sent to ${m.email}.`)}>{m.invitedAt ? "Resend invite" : "Invite"}</Button>}
                  {m.active
                    ? <Button size="sm" kind="quiet" disabled={busy} onClick={() => act(() => freight.staff.removeMember(carrier.id, m.id), `${m.email} can no longer sign in.`)}>Remove</Button>
                    : <Button size="sm" kind="quiet" disabled={busy} onClick={() => act(() => freight.staff.updateMember(carrier.id, m.id, { active: true }))}>Restore</Button>}
                </span>
              </li>
            ))}
          </ul>
        ) : <div style={{ fontSize: 13.5, color: T.ink3 }}>Nobody yet. Add the owner first.</div>}
        <form onSubmit={(e) => { e.preventDefault(); act(async () => {
          // A recruiter is a dispatcher-role member who opens Hiring only (backend access.mjs).
          const body = f.role === "recruiter" && !f.owner ? { ...f, role: "dispatcher", access: "hiring" } : f;
          await freight.staff.addMember(carrier.id, body); setF({ email: "", name: "", phone: "", role: "dispatcher", owner: false });
        }, "Added."); }}
          style={{ display: "grid", gap: 10, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 12 }}>
          <Micro>Add someone</Micro>
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <Input label="Email" type="email" value={f.email} onChange={set("email")} />
            <Input label="Name" value={f.name} onChange={set("name")} />
            <Input label="Cell" type="tel" value={f.phone} onChange={set("phone")} />
            <Select label="Role" value={f.owner ? "manager" : f.role} onChange={set("role")} options={[{ v: "dispatcher", label: "Dispatcher" }, { v: "recruiter", label: "Recruiter (hiring only)" }, { v: "manager", label: "Manager (admin)" }]} />
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
            <input type="checkbox" checked={f.owner} onChange={(e) => setF((x) => ({ ...x, owner: e.target.checked, role: e.target.checked ? "manager" : x.role }))} />
            The owner (a manager)
          </label>
          <div><Button type="submit" size="sm" disabled={busy || !f.email.trim()}>Add</Button></div>
        </form>
        <Note msg={msg} />
      </div>
    </Panel>
  );
}

/* ── drivers and past applicants: imports with the carrier's statement ───────── */

function ConsentFields({ value, onChange, what }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div style={{ display: "grid", gap: 10, border: `1px solid ${T.hairSoft}`, borderRadius: 8, padding: 12 }}>
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, lineHeight: 1.45 }}>
        <input type="checkbox" checked={value.on} onChange={(e) => onChange({ ...value, on: e.target.checked })} style={{ marginTop: 3 }} />
        <span>The carrier told us these {what} agreed to automated calls. <span style={{ color: T.ink3 }}>Recorded as their statement, with who said it and where. Leave unticked if they did not say so: nobody here is called until consent is on record.</span></span>
      </label>
      {value.on && (
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Input label="What they said" value={value.statement} onChange={set("statement")} placeholder="Every driver agreed in their onboarding paperwork" />
          <Input label="Who at the carrier said it" value={value.saidBy} onChange={set("saidBy")} />
          <Input label="Where" value={value.saidIn} onChange={set("saidIn")} placeholder="their email of October 29" />
        </div>
      )}
    </div>
  );
}

/** UTF-8 first; a sheet Excel saved as Windows text comes back with replacement characters, so it is read again as that. */
function readFile(file) {
  const read = (enc) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result || "")); r.onerror = reject; r.readAsText(file, enc); });
  return read("utf-8").then((t) => (t.includes("\uFFFD") ? read("windows-1252") : t));
}

export function ImportPanel({ carrier, kind, title, note, onImported, children }) {
  // ⛔ Past applicants never get call consent here (Brian's decision 7): Alfred texts them first.
  const takesConsent = kind === "drivers";
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState({ on: false, statement: "", saidBy: "", saidIn: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const api = kind === "drivers"
    ? { preview: freight.staff.previewDrivers, apply: freight.staff.importDrivers }
    : { preview: freight.staff.previewApplicants, apply: freight.staff.importApplicants };
  const run = async (fn) => { setBusy(true); setMsg(null); try { await fn(); } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); } };
  return (
    <Panel id={`${kind}-title`} title={title} note={note}>
      <div style={{ display: "grid", gap: 12 }}>
        {children}
        <label style={{ display: "grid", gap: 4, fontSize: 12.5, color: T.ink2 }}>
          The sheet they sent back (CSV)
          <input type="file" accept=".csv,text/csv" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setCsv(await readFile(f)); setPreview(null); } }} />
        </label>
        <TextArea label="Or paste it" value={csv} onChange={(v) => { setCsv(v); setPreview(null); }} rows={4} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="sm" kind="quiet" disabled={busy || !csv.trim()} onClick={() => run(async () => setPreview(await api.preview(carrier.id, csv)))}>Check the sheet</Button>
        </div>
        {preview && (
          <div style={{ fontSize: 13, display: "grid", gap: 6 }}>
            <div>{preview.wouldImport} {preview.wouldImport === 1 ? "row" : "rows"} will import{preview.errors.length ? `, ${preview.errors.length} will not:` : "."}</div>
            {!!preview.errors.length && <ul style={{ margin: 0, paddingLeft: 18, color: T.ink2 }}>{preview.errors.slice(0, 12).map((e, i) => <li key={i}>Line {e.line}: {e.message}</li>)}</ul>}
            {takesConsent && (
              <>
                <div style={{ fontSize: 12.5, color: T.ink2 }}>
                  {preview.consentColumn
                    ? "The sheet has a Consent column: only the drivers it marks yes are recorded as agreed. A no stays a no."
                    : "The sheet has no Consent column: the carrier’s statement, if you record it, covers every driver in it."}
                </div>
                <ConsentFields value={consent} onChange={setConsent} what="drivers" />
              </>
            )}
            {!takesConsent && <div style={{ fontSize: 12.5, color: T.ink2 }}>They are imported without call consent: Alfred texts each one first and calls only those who say yes.</div>}
            <div>
              <Button size="sm" disabled={busy || !preview.wouldImport} onClick={() => run(async () => {
                const out = await api.apply(carrier.id, csv, takesConsent && consent.on ? { statement: consent.statement, saidBy: consent.saidBy, saidIn: consent.saidIn } : null);
                setMsg({ ok: true, text: `${out.imported} imported${out.errors.length ? `, ${out.errors.length} not` : ""}.` });
                setCsv(""); setPreview(null); onImported();
              })}>Import {preview.wouldImport}</Button>
            </div>
          </div>
        )}
        <Note msg={msg} />
      </div>
    </Panel>
  );
}

export function DriversList({ carrier, drivers, onChanged }) {
  const [msg, setMsg] = useState(null);
  const mark = async (d, on) => {
    setMsg(null);
    try { await freight.staff.markTestPhone(carrier.id, "drivers", d.id, on); onChanged(); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
  };
  if (!drivers.length) return <div style={{ fontSize: 13.5, color: T.ink3 }}>No drivers yet.</div>;
  // Test phones are for the rehearsal: marked in setup or rehearsal, and cleared when the carrier goes live.
  const rehearsing = carrier.stage === "setup" || carrier.stage === "rehearsal";
  const real = drivers.filter((d) => !d.testPhone && !d.persona);
  const consented = real.filter((d) => d.consent).length;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontSize: 13.5 }}>{real.length} on the roster, {consented} with consent on record{drivers.length > real.length ? ` (and ${drivers.length - real.length} test or AI ${drivers.length - real.length === 1 ? "phone" : "phones"})` : ""}.</div>
      <div style={{ fontSize: 12.5, color: T.ink3 }}>A test phone is one our own team answers: in rehearsal Alfred calls only those. Never mark a driver’s phone.</div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 320, overflow: "auto" }}>
        {drivers.map((d) => (
          <li key={d.id} style={{ display: "flex", gap: "4px 12px", alignItems: "center", flexWrap: "wrap", padding: "7px 0", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13 }}>
            <span style={{ flex: "1 1 160px", fontWeight: 500 }}>{d.name}</span>
            <span style={{ flex: "0 1 130px", fontFamily: mono, fontSize: 12 }}>{fmtPhone(d.phoneE164)}</span>
            <span style={{ flex: "1 1 140px", color: T.ink2 }}>{d.consent ? "Consent on record" : "No consent: not called"}</span>
            <label style={{ display: "flex", gap: 6, alignItems: "center", color: rehearsing ? T.ink : T.ink3 }}>
              <input type="checkbox" checked={!!d.testPhone} disabled={!rehearsing && !d.testPhone} onChange={(e) => mark(d, e.target.checked)} />
              Test phone
            </label>
          </li>
        ))}
      </ul>
      <Note msg={msg} />
    </div>
  );
}

/* ── hiring ──────────────────────────────────────────────────────────────────── */

const CDL = [{ v: "A", label: "Class A" }, { v: "B", label: "Class B" }, { v: "C", label: "Class C" }];
const ENDORSE = [["H", "Hazmat"], ["N", "Tanker"], ["T", "Doubles/triples"], ["X", "Tanker + hazmat"]];

export function HiringPanel({ carrier, settings, onSaved }) {
  const r = settings?.recruiter || {};
  const c = settings?.criteria || {};
  const [name, setName] = useState(r.name || "");
  const [phone, setPhone] = useState(r.phoneE164 ? fmtPhone(r.phoneE164) : "");
  const [email, setEmail] = useState(r.email || "");
  const [cdl, setCdl] = useState(c.cdlClass || "A");
  const [years, setYears] = useState(c.minMonths != null ? String(Math.round(c.minMonths / 12 * 10) / 10) : "");
  const [states, setStates] = useState((c.states || []).join(", "));
  const [ends, setEnds] = useState(c.endorsements || []);
  // The three the recruiter checks (backend recruitingLib.mjs normaliseCriteria): shown on the
  // applicant page as the carrier wrote them, never scored.
  const [homeTime, setHomeTime] = useState(c.homeTime || "");
  const [record, setRecord] = useState(c.record || "");
  const [drugTests, setDrugTests] = useState(c.drugTests || "");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  // Two saves, so saving the recruiter never records the standards as the carrier's answer.
  const save = async (patch, ok) => {
    setBusy(true); setMsg(null);
    try { onSaved(await freight.staff.saveRecruiting(carrier.id, patch)); setMsg({ ok: true, text: ok }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };
  const saveRecruiter = () => save({ recruiter: { name, phone, email } }, "The recruiter is saved.");
  const saveStandards = () => {
    const months = years === "" ? undefined : Math.round(Number(years) * 12);
    return save({ criteria: { cdlClass: cdl, ...(Number.isFinite(months) ? { minMonths: months } : {}), endorsements: ends,
      states: states.split(/[\s,]+/).filter(Boolean), homeTime, record, drugTests } }, "The hiring standards are saved.");
  };
  return (
    <Panel id="hiring-title" title="Hiring" note={settings?.live ? (r.transferReady ? "Live transfer to the recruiter is ready." : "Live transfer is not set up yet.") : "The recruiter is registered for live transfers when the carrier goes live."}>
      <div style={{ display: "grid", gap: 12 }}>
        <Micro>The recruiter</Micro>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          <Input label="Name" value={name} onChange={setName} />
          <Input label="Cell" type="tel" value={phone} onChange={setPhone} />
          <Input label="Email" type="email" value={email} onChange={setEmail} />
        </div>
        <div><Button size="sm" disabled={busy} onClick={saveRecruiter}>Save the recruiter</Button></div>
        <Micro>Hiring standards</Micro>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          <Select label="CDL" value={cdl} onChange={setCdl} options={CDL} />
          <Input label="Years driving, at least" type="number" value={years} onChange={setYears} />
          <Input label="States they hire in (blank = anywhere)" value={states} onChange={setStates} placeholder="TX, OK, LA" />
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13 }}>
          {ENDORSE.map(([k, label]) => (
            <label key={k} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={ends.includes(k)} onChange={(e) => setEnds(e.target.checked ? [...ends, k] : ends.filter((x) => x !== k))} />
              {label}
            </label>
          ))}
        </div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <Input label="Home time" value={homeTime} onChange={setHomeTime} placeholder="Home every other weekend" />
          <Input label="Driving record limits" value={record} onChange={setRecord} placeholder="No preventable accidents in 3 years" />
          <Input label="Drug and alcohol testing" value={drugTests} onChange={setDrugTests} placeholder="None failed or refused in 2 years" />
        </div>
        <div style={{ fontSize: 12.5, color: T.ink3 }}>
          {settings?.criteriaSetAt ? `Saved by ${settings.criteriaSetBy || "someone"}, ${fmtDate(settings.criteriaSetAt)}.` : "Not saved yet: these are the defaults, not the carrier’s answer."}
          {" "}Alfred never asks about driving records or drug tests: the applicant page shows these to the recruiter, who checks them.
        </div>
        <div><Button size="sm" disabled={busy} onClick={saveStandards}>Save the standards</Button></div>
        <Note msg={msg} />
      </div>
    </Panel>
  );
}

/* ── the opt-in link and billing ────────────────────────────────────────────── */

/**
 * The opt-in link the setup email promises the carrier, and the plan and billing contact the
 * carrier's Billing page shows (backend optin.mjs, billing.mjs). No payment processor is connected.
 */
export function LinkAndBillingPanel({ carrier, initialLink, initialBilling }) {
  const [link, setLink] = useState(initialLink);
  const [b, setB] = useState(initialBilling ? { plan: initialBilling.plan || "", price: initialBilling.priceCents != null ? String(initialBilling.priceCents / 100) : "", billingEmail: initialBilling.billingEmail || "" } : null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);
  useEffect(() => {
    if (initialLink !== undefined && initialBilling !== undefined) return undefined;
    let alive = true;
    freight.staff.optInLink(carrier.id).then((u) => alive && setLink(u)).catch(() => alive && setLink(null));
    freight.staff.billing(carrier.id).then((x) => alive && setB({ plan: x.plan || "", price: x.priceCents != null ? String(x.priceCents / 100) : "", billingEmail: x.billingEmail || "" }))
      .catch(() => alive && setB({ plan: "", price: "", billingEmail: "" }));
    return () => { alive = false; };
  }, [carrier.id]);
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await freight.staff.saveBilling(carrier.id, { plan: b.plan, priceCents: b.price === "" ? null : Math.round(Number(b.price) * 100), billingEmail: b.billingEmail });
      setMsg({ ok: true, text: "Billing saved." });
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const replace = async () => {
    setBusy(true); setMsg(null); setReplacing(false);
    try { setLink(await freight.staff.newOptInLink(carrier.id)); setMsg({ ok: true, text: "A new opt-in link is ready. The old one no longer works." }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Panel id="link-title" title="The opt-in link, and billing" note="The link goes to the carrier to share with its drivers, dispatchers and recruiters. Billing shows on their Billing page; no card can be taken yet.">
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontSize: 13.5, overflowWrap: "anywhere" }}>{link === undefined ? "Loading…" : link || "FRONTEND_URL is not set on the server, so there is no link to share."}</div>
        {link && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {replacing
              ? <><Button size="sm" kind="quiet" disabled={busy} onClick={replace}>Yes, replace the link</Button><span style={{ fontSize: 13 }}>The old link stops working at once.</span></>
              : <Button size="sm" kind="quiet" disabled={busy} onClick={() => setReplacing(true)}>Replace the link</Button>}
          </div>
        )}
        {b && (
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <Input label="Plan" value={b.plan} onChange={(v) => setB((x) => ({ ...x, plan: v }))} />
            <Input label="Monthly price ($)" type="number" value={b.price} onChange={(v) => setB((x) => ({ ...x, price: v }))} />
            <Input label="Bills go to" type="email" value={b.billingEmail} onChange={(v) => setB((x) => ({ ...x, billingEmail: v }))} />
          </div>
        )}
        {b && <div><Button size="sm" disabled={busy} onClick={save}>Save billing</Button></div>}
        <Note msg={msg} />
      </div>
    </Panel>
  );
}

/* ── moving on: rehearsal, live, pause ──────────────────────────────────────── */

function CheckList({ title, items, onTick, busy }) {
  return (
    <div>
      <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>{title}</h3>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {items.map((c) => (
          <li key={c.id} style={{ display: "flex", gap: "4px 14px", alignItems: "baseline", flexWrap: "wrap", padding: "8px 0", borderTop: `1px solid ${T.hairSoft}` }}>
            <span style={{ flex: "0 0 70px", fontSize: 12.5, fontWeight: 600, color: c.ok ? T.ink3 : T.ink }}>{c.ok ? "✓ Done" : "Open"}</span>
            <span style={{ flex: "1 1 260px", fontSize: 13.5 }}>{c.label}{c.detail ? <span style={{ color: T.ink3 }}> · {c.detail}</span> : null}</span>
            {c.tick && <Button size="sm" kind="quiet" disabled={busy} onClick={() => onTick(c.id, !c.ok)}>{c.ok ? "Untick" : "Tick, with my name"}</Button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GoLivePanel({ carrier, gates, members, onChanged }) {
  const [pick, setPick] = useState(() => new Set());
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true); setMsg(null);
    try { const text = await fn(); setMsg(text ? { ok: true, text } : null); onChanged(); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };
  const tick = (id, done) => run(async () => { await freight.staff.tick(carrier.id, id, done); return null; });
  const move = (stage, memberIds) => run(async () => {
    const out = await freight.staff.moveTo(carrier.id, stage, memberIds ? { memberIds } : {});
    const sent = (out.invites || []).filter((i) => i.sent).length;
    const unsent = (out.invites || []).filter((i) => !i.sent);
    return `${STAGE_LABEL[out.carrier.stage]}.${out.invites?.length ? ` Invites: ${sent} sent${unsent.length ? `, ${unsent.length} not or not sure (${unsent[0].message || mailError(unsent[0].error)})` : ""}.` : ""}${out.recruiter?.note ? ` ${out.recruiter.note}` : ""}`;
  });
  const active = members.filter((m) => m.active);
  const can = (to) => gates.moves.includes(to);
  return (
    <Panel id="golive-title" title="Before go-live" right={<Tag solid={carrier.stage === "live"}>{STAGE_LABEL[carrier.stage]}</Tag>}
      note="The server checks each list from the carrier’s records. A move is refused until its list is complete.">
      <div style={{ display: "grid", gap: 16 }}>
        <CheckList title="To rehearsal" items={gates.rehearsal} busy={busy} onTick={tick} />
        <CheckList title="To live" items={gates.live} busy={busy} onTick={tick} />
        {can("rehearsal") && (
          <div style={{ display: "grid", gap: 8 }}>
            <Micro>Who to invite to the rehearsal</Micro>
            {active.length ? active.map((m) => (
              <label key={m.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                <input type="checkbox" checked={pick.has(m.id)} onChange={(e) => setPick((s) => { const n = new Set(s); if (e.target.checked) n.add(m.id); else n.delete(m.id); return n; })} />
                {m.name || m.email} <span style={{ color: T.ink3 }}>· {roleLabel(m)}</span>
              </label>
            )) : <div style={{ fontSize: 12.5, color: T.ink3 }}>Add their people first.</div>}
            <div><Button disabled={busy || !gates.ready.rehearsal} onClick={() => move("rehearsal", [...pick])}>Move to rehearsal</Button></div>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {can("live") && <Button disabled={busy || !gates.ready.live} onClick={() => move("live")}>{carrier.stage === "paused" ? "Resume: go live again" : "Go live and send sign-in links"}</Button>}
          {can("paused") && <Button kind="quiet" disabled={busy} onClick={() => move("paused")}>Pause the carrier</Button>}
          {can("setup") && <Button kind="quiet" disabled={busy} onClick={() => move("setup")}>Back to setup</Button>}
        </div>
        <Note msg={msg} />
      </div>
    </Panel>
  );
}

/* ── history ─────────────────────────────────────────────────────────────────── */

export function HistoryPanel({ carrierId }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => freight.staff.audit(carrierId).then(setRows).catch((e) => setErr(errText(e)));
  return (
    <Panel id="history-title" title="History" note="Every change we made to this carrier, newest first.">
      {!rows ? <Button size="sm" kind="quiet" onClick={load}>Show the history</Button> : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 360, overflow: "auto" }}>
          {rows.map((a) => (
            <li key={a.id} style={{ display: "flex", gap: "2px 12px", flexWrap: "wrap", padding: "6px 0", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5 }}>
              <span style={{ flex: "0 0 150px", color: T.ink3 }}>{new Date(a.at).toLocaleString("en-US")}</span>
              <span style={{ flex: "0 0 150px", fontFamily: mono }}>{a.action}</span>
              {/* A change the carrier's own admin made (Admins and access) says so: it is theirs, not ours. */}
              <span style={{ flex: "1 1 200px", color: T.ink2 }}>{a.staffEmail}{a.byCarrier ? " (their admin)" : ""}</span>
            </li>
          ))}
        </ul>
      )}
      {err && <Note msg={{ ok: false, text: err }} />}
    </Panel>
  );
}

/* ── the page ────────────────────────────────────────────────────────────────── */

export default function StaffCarrierSetup() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try {
      const [carrier, setup, members, drivers, settings, emails, gates] = await Promise.all([
        freight.staff.getCarrier(id), freight.staff.setup(id), freight.staff.members(id), freight.staff.drivers(id),
        freight.staff.recruiting(id), freight.staff.emails(id), freight.staff.gates(id),
      ]);
      setD({ carrier, setup, members, drivers, settings, emails, gates });
    } catch (e) { setErr(errText(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  return (
    <StaffShell active="carriers">
      <Link to="/staff" className="stf-link" style={{ fontSize: 13.5, color: T.ink2 }}>← Carriers</Link>
      <Note msg={err ? { ok: false, text: err } : null} />
      {!d && !err && <div style={{ color: T.ink3, fontSize: 13.5 }}>Loading…</div>}
      {d && <CarrierSetupBody d={d} reload={load} onSetup={(setup) => setD((x) => ({ ...x, setup }))} />}
    </StaffShell>
  );
}

export function CarrierSetupBody({ d, reload, onSetup }) {
  const { carrier, setup, members, drivers, settings, emails, gates } = d;
  return (
    <>
      <div>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: -0.7 }}>{carrier.name}</h1>
        <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 6 }}>
          {[carrier.mcNumber && `MC ${carrier.mcNumber}`, carrier.dotNumber && `USDOT ${carrier.dotNumber}`, STAGE_LABEL[carrier.stage],
            carrier.staffCreatedBy ? `Added by ${carrier.staffCreatedBy}` : "Set itself up"].filter(Boolean).join(" · ")}
        </div>
      </div>
      <SetupEmailPanel carrier={carrier} emails={emails} missingCount={setup.missing.length} onSent={reload} />
      <AnswersPanel setup={setup} />
      {/* A saved answer can open or close the go-live list, so everything is read again. */}
      <QuestionsPanel carrierId={carrier.id} setup={setup} onSaved={(s) => { onSetup(s); reload(); }} />
      <DetailsPanel carrier={carrier} onSaved={reload} />
      <PeoplePanel carrier={carrier} members={members} onChanged={reload} />
      <ImportPanel carrier={carrier} kind="drivers" title="Drivers" onImported={reload}
        note="Import the drivers sheet they sent back. Its columns are the ones this import reads.">
        <DriversList carrier={carrier} drivers={drivers} onChanged={reload} />
      </ImportPanel>
      <ImportPanel carrier={carrier} kind="applicants" title="Past applicants" onImported={reload}
        note="Their export of past applicants: name, phone and date applied. Alfred texts each one first and calls only those who say yes, once that is built (pilot item 15)." />
      <HiringPanel carrier={carrier} settings={settings} onSaved={reload} />
      <LinkAndBillingPanel carrier={carrier} />
      <GoLivePanel carrier={carrier} gates={gates} members={members} onChanged={reload} />
      <HistoryPanel carrierId={carrier.id} />
    </>
  );
}
